import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Package, Search, RefreshCw, Hash, Pencil, X, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ALL_TAGS = ["Kendi Gelir", "Transferli", "VIP", "Cam Kenarı", "Alkollü", "Alkolsüz", "Limitli", "Limitsiz", "Snack", "Yemeksiz"];

const TAG_STYLES = {
  "Kendi Gelir": "bg-gray-200 text-gray-700",
  "Transferli": "bg-sky-100 text-sky-700",
  "VIP": "bg-fuchsia-100 text-fuchsia-700",
  "Cam Kenarı": "bg-violet-100 text-violet-700",
  "Alkollü": "bg-rose-100 text-rose-700",
  "Alkolsüz": "bg-emerald-100 text-emerald-700",
  "Limitli": "bg-amber-100 text-amber-700",
  "Limitsiz": "bg-orange-100 text-orange-700",
  "Snack": "bg-lime-100 text-lime-700",
  "Yemeksiz": "bg-slate-200 text-slate-600",
};

const CAT_STYLES = {
  "Dinner Cruise": "bg-indigo-100 text-indigo-700",
  "Sunset Cruise": "bg-orange-100 text-orange-700",
  "Small Group Sunset": "bg-amber-100 text-amber-700",
  "Daytime": "bg-cyan-100 text-cyan-700",
};

export default function ProductCatalog() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [defters, setDefters] = useState([]);
  const [tagEdit, setTagEdit] = useState(null); // {product, draft:Set}
  const [savingKey, setSavingKey] = useState(null);
  const headers = useMemo(() => ({ Authorization: `Bearer ${localStorage.getItem("auth_token")}` }), []);

  const load = (refresh = false) => {
    setLoading(true);
    axios.get(`${API}/api/products/catalog${refresh ? "?refresh=true" : ""}`, { headers })
      .then(({ data }) => setProducts(data.products || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    axios.get(`${API}/api/defters`, { headers }).then(({ data }) => setDefters(data.defters || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setCategory = async (product, catId) => {
    const cid = Number(catId);
    setSavingKey(product.override_key + "-cat");
    try {
      await axios.post(`${API}/api/category-mappings`, { product_name: product.product_name, category_id: cid }, { headers });
      const cname = (defters.find((d) => d.id === cid) || {}).name || "";
      setProducts((ps) => ps.map((p) => p.override_key === product.override_key ? { ...p, category_id: cid, category_name: cname } : p));
      toast.success(`"${product.product_name}" → ${cname} defterine taşındı`);
    } catch { toast.error("Kategori değiştirilemedi"); }
    setSavingKey(null);
  };

  const setHotel = async (product, value) => {
    // value: "" (otomatik) | "KENDİ GELİR" | "HOTEL?"
    const pid = product.bokun_product_id;
    if (!pid) { toast.error("Bu ürünün Bókun ID'si yok, override uygulanamaz"); return; }
    setSavingKey(product.override_key + "-otel");
    try {
      await axios.put(`${API}/api/product-hotel-overrides`, { product_id: pid, value }, { headers });
      // Ayni Bókun ID'sine sahip TUM satirlari guncelle (bir urun id'si birden cok varyant kapsayabilir)
      setProducts((ps) => ps.map((p) => String(p.bokun_product_id) === String(pid)
        ? { ...p, otel_tipi: value || (p.otel_tipi_auto || ""), otel_override: !!value } : p));
      toast.success(value ? `Otel tipi → "${value}" (tüm rezervasyonlar)` : "Otel tipi otomatiğe döndürüldü");
    } catch { toast.error("Otel tipi kaydedilemedi"); }
    setSavingKey(null);
  };

  const openTagEdit = (product) => setTagEdit({ product, draft: new Set(product.tags || []) });
  const toggleTag = (t) => setTagEdit((te) => {
    const d = new Set(te.draft);
    d.has(t) ? d.delete(t) : d.add(t);
    return { ...te, draft: d };
  });
  const saveTags = async (reset = false) => {
    const product = tagEdit.product;
    const tags = reset ? [] : [...tagEdit.draft];
    setSavingKey(product.override_key + "-tags");
    try {
      await axios.put(`${API}/api/product-tag-overrides`, { key: product.override_key, tags }, { headers });
      setProducts((ps) => ps.map((p) => p.override_key === product.override_key
        ? { ...p, tags: reset ? (p.tags_derived || []) : tags, tags_override: !reset } : p));
      toast.success(reset ? "Etiketler türetilmiş haline döndürüldü" : "Etiketler güncellendi");
      setTagEdit(null);
    } catch { toast.error("Etiketler kaydedilemedi"); }
    setSavingKey(null);
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return products;
    return products.filter((p) =>
      (p.product_name || "").toLowerCase().includes(s) ||
      (p.bokun_title || "").toLowerCase().includes(s) ||
      String(p.bokun_product_id || "").includes(s) ||
      (p.category_name || "").toLowerCase().includes(s) ||
      (p.tags || []).some((t) => t.toLowerCase().includes(s)));
  }, [products, q]);

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="product-catalog-page">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-violet-600 text-white"><Package className="w-6 h-6" /></div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Ürün Tanımları (Bókun)</h1>
            <p className="text-sm text-slate-500">Her ürünün Bókun ID'si, kategorisi, etiketleri ve nasıl göründüğü</p>
          </div>
        </div>
        <button onClick={() => load(true)} disabled={loading}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold disabled:opacity-50"
          data-testid="catalog-refresh-btn">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Yenile
        </button>
      </div>

      <div className="relative mt-5 mb-4 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Ürün adı, Bókun ID, kategori veya etiket ara…"
          className="w-full pl-9 pr-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-violet-500 outline-none text-sm"
          data-testid="catalog-search-input"
        />
      </div>

      {loading ? (
        <div className="py-24 text-center text-slate-400" data-testid="catalog-loading">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3" /> Ürünler yükleniyor… (Bókun taranıyor)
        </div>
      ) : (
        <>
          <div className="text-xs text-slate-500 mb-2" data-testid="catalog-count">
            {filtered.length} ürün {q ? `(${products.length} içinden)` : ""}
          </div>
          <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm bg-white">
            <table className="w-full text-sm" data-testid="catalog-table">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Bókun ID</th>
                  <th className="text-left px-4 py-3 font-semibold">Ürün Adı</th>
                  <th className="text-left px-4 py-3 font-semibold">Kategori (Defter)</th>
                  <th className="text-left px-4 py-3 font-semibold">Otel Tipi</th>
                  <th className="text-left px-4 py-3 font-semibold">Etiketler</th>
                  <th className="text-left px-4 py-3 font-semibold">Menü</th>
                  <th className="text-right px-4 py-3 font-semibold">Rez.</th>
                  <th className="text-left px-4 py-3 font-semibold">Örnek Müşteri</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((p, i) => (
                  <tr key={p.override_key || p.product_name} className="hover:bg-violet-50/40 transition-colors"
                    data-testid={`catalog-row-${i}`}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {p.bokun_product_id ? (
                        <span className="inline-flex items-center gap-1 font-mono text-[12px] font-bold text-violet-700 bg-violet-50 px-2 py-0.5 rounded">
                          <Hash className="w-3 h-3" />{p.bokun_product_id}
                        </span>
                      ) : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{p.product_name}</div>
                      {p.bokun_title && p.bokun_title.toLowerCase() !== (p.product_name || "").toLowerCase() && (
                        <div className="text-[11px] text-slate-400">Bókun: {p.bokun_title}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <select
                        value={p.category_id ?? ""}
                        onChange={(e) => setCategory(p, e.target.value)}
                        disabled={savingKey === p.override_key + "-cat"}
                        className={`text-[11px] font-semibold px-2 py-1 rounded-lg border cursor-pointer outline-none ${CAT_STYLES[p.category_name] || "bg-slate-100 text-slate-500"} border-transparent focus:border-violet-400`}
                        data-testid={`catalog-cat-select-${i}`}>
                        <option value="" disabled>— Eşleşmemiş —</option>
                        {defters.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <select
                          value={p.otel_override ? p.otel_tipi : ""}
                          onChange={(e) => setHotel(p, e.target.value)}
                          disabled={savingKey === p.override_key + "-otel" || !p.bokun_product_id}
                          className={`text-[11px] border rounded-md px-1.5 py-1 bg-white ${p.otel_override ? "border-violet-300 text-violet-700 font-semibold" : "border-slate-200 text-slate-600"}`}
                          data-testid={`catalog-otel-select-${i}`}>
                          <option value="">Otomatik{p.otel_tipi_auto ? ` (${p.otel_tipi_auto === "HOTEL?" ? "Transfer" : p.otel_tipi_auto})` : ""}</option>
                          <option value="KENDİ GELİR">KENDİ GELİR</option>
                          <option value="HOTEL?">Transfer (otel?)</option>
                        </select>
                        {p.otel_override && <span className="text-[9px] font-bold text-violet-500 uppercase" title="Elle geçersiz kılındı">·manuel</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        {(p.tags || []).length ? p.tags.map((t) => (
                          <span key={t} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${TAG_STYLES[t] || "bg-slate-100 text-slate-600"}`}>{t}</span>
                        )) : <span className="text-slate-300 text-xs">—</span>}
                        {p.tags_override && <span className="text-[9px] font-bold text-violet-500 uppercase" title="Elle geçersiz kılındı">·manuel</span>}
                        <button onClick={() => openTagEdit(p)} title="Etiketleri düzenle"
                          className="ml-1 p-1 rounded hover:bg-violet-100 text-slate-400 hover:text-violet-600"
                          data-testid={`catalog-tag-edit-${i}`}>
                          <Pencil className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-[11px] font-mono text-slate-500">{(p.menu || []).join(", ") || "—"}</span>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-700">{p.count}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{p.sample_customer || "—"}</td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr><td colSpan={8} className="px-4 py-16 text-center text-slate-400">Ürün bulunamadı</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tagEdit && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={() => setTagEdit(null)} data-testid="tag-edit-overlay">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="tag-edit-modal">
            <div className="flex items-center justify-between px-5 py-3.5 bg-violet-600 text-white">
              <span className="font-semibold text-sm">Etiketleri Düzenle</span>
              <button onClick={() => setTagEdit(null)} className="p-1 rounded-lg hover:bg-white/20" data-testid="tag-edit-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-xs text-slate-500 truncate">{tagEdit.product.product_name}</div>
              <div className="flex flex-wrap gap-2" data-testid="tag-edit-options">
                {ALL_TAGS.map((t) => {
                  const on = tagEdit.draft.has(t);
                  return (
                    <button key={t} onClick={() => toggleTag(t)}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-full border-2 transition-colors ${on ? `${TAG_STYLES[t]} border-transparent` : "bg-white text-slate-400 border-slate-200 hover:border-slate-300"}`}
                      data-testid={`tag-opt-${t}`}>
                      {on && <Check className="w-3 h-3 inline mr-1" />}{t}
                    </button>
                  );
                })}
              </div>
              <div className="text-[11px] text-slate-400">Türetilmiş: {(tagEdit.product.tags_derived || []).join(", ") || "—"}</div>
              <div className="flex gap-2 pt-1">
                <button onClick={() => saveTags(true)} disabled={!!savingKey}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 disabled:opacity-40"
                  data-testid="tag-edit-reset">Otomatike Dön</button>
                <button onClick={() => saveTags(false)} disabled={!!savingKey}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-40"
                  data-testid="tag-edit-save">{savingKey ? "Kaydediliyor…" : "Kaydet"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
